/**
 * <biosphere-cart-discount> — discount code entry for the markup rendered by
 * snippets/biosphere-cart-discount.liquid.
 *
 * Applies through the platform's cart update endpoint, which takes a `discount`
 * parameter: one code, several comma-separated, or an empty string to clear. There is
 * no remove-one call, so removing a code re-sends the ones that remain.
 *
 * Whether a code took is read off the re-rendered section, not the JSON: the endpoint
 * accepts any string and answers 200 either way, and the Ajax cart JSON carries no
 * discount_applications field. The server-rendered chips are the answer.
 *
 * On success it publishes `biosphere:cart:change` and lets every cart surface
 * re-render itself — it never swaps their HTML directly, because this element lives
 * inside one of those surfaces and would be destroyed mid-flight.
 *
 * On a rejected code it deliberately does NOT publish: a re-render would replace this
 * element, closing the disclosure and taking the error message with it.
 */
import { sectionUrl, sectionsUrlContext } from "biosphere-section-url";

class BiosphereCartDiscount extends HTMLElement {
  #abort;
  #input;
  #error;
  #live;

  connectedCallback() {
    const disclosure = this.querySelector("[data-disclosure]");
    this.#input = this.querySelector("[data-input]");
    this.#error = this.querySelector("[data-error]");
    this.#live = this.querySelector(".biosphere-cart-discount__live");

    // Nothing to enhance without the field; the note stands on its own.
    if (!disclosure || !this.#input) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener("click", (event) => this.#onClick(event), { signal });
    this.#input.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter") return;
        // The field sits inside the cart form; Enter would submit it.
        event.preventDefault();
        this.#apply();
      },
      { signal },
    );

    // Reveal the JS-only affordances, and retire the no-JS note.
    disclosure.hidden = false;
    for (const remove of this.querySelectorAll("[data-remove]")) {
      remove.hidden = false;
    }
    const note = this.querySelector("[data-note]");
    if (note) note.hidden = true;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onClick(event) {
    if (event.target.closest("[data-apply]")) {
      this.#apply();
      return;
    }
    const remove = event.target.closest("[data-remove]");
    if (!remove) return;
    const code = remove.closest("[data-code]")?.dataset.code;
    if (code) this.#remove(code);
  }

  /** Codes currently on the cart, as the server rendered them. */
  #appliedCodes(root = this) {
    return [...root.querySelectorAll("[data-code]")].map(
      (item) => item.dataset.code,
    );
  }

  #apply() {
    const entered = this.#input.value.trim();
    // Saying nothing to a click reads as a broken button, so the empty
    // field answers rather than ignoring it.
    if (!entered) {
      this.#showError(this.dataset.requiredMessage);
      this.#input.focus();
      return;
    }

    const existing = this.#appliedCodes();
    const duplicate = existing.some(
      (code) => code.toLowerCase() === entered.toLowerCase(),
    );
    // Codes are case-insensitive, so re-sending one already on the cart
    // would be a no-op that reads as a silent failure.
    if (duplicate) {
      this.#input.value = "";
      return;
    }

    this.#send([...existing, entered], entered);
  }

  #remove(code) {
    const remaining = this.#appliedCodes().filter(
      (applied) => applied.toLowerCase() !== code.toLowerCase(),
    );
    this.#send(remaining, null);
  }

  async #send(codes, entered) {
    this.#clearError();
    this.setAttribute("aria-busy", "true");

    const sectionId = this.dataset.sectionId;
    let cart;
    try {
      const response = await fetch(`${this.dataset.cartUpdateUrl}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: codes.join(","),
          sections: sectionId,
          sections_url: sectionsUrlContext(),
        }),
      });
      cart = await response.json();
    } catch {
      this.removeAttribute("aria-busy");
      this.#showError(this.dataset.errorMessage);
      return;
    }

    const html = await this.#sectionHtml(cart, sectionId);
    this.removeAttribute("aria-busy");

    if (entered && html) {
      const fetched = new DOMParser().parseFromString(html, "text/html");
      const applied = this.#appliedCodes(fetched).map((code) =>
        code.toLowerCase(),
      );
      if (!applied.includes(entered.toLowerCase())) {
        this.#showError(this.dataset.invalidMessage);
        return;
      }
    }

    this.#input.value = "";
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:change", {
        detail: {
          cart,
          action: "discount",
          source: this.dataset.source,
          sectionsApplied: [],
        },
      }),
    );
  }

  /**
   * This section's fresh HTML: bundled in the update response when the endpoint
   * honoured `sections`, fetched when it did not. Null if neither worked, in which
   * case the caller treats the change as applied rather than rejecting a good code.
   */
  async #sectionHtml(cart, sectionId) {
    if (!sectionId) return null;
    const bundled = cart?.sections?.[sectionId];
    if (bundled) return bundled;

    try {
      const response = await fetch(
        // The page's own query string is kept and `sections` added to it. Built
        // from pathname alone, this asked the server for a different page than
        // the one being viewed: /collections/all?view=black-friday re-rendered
        // as /collections/all, and anything else carried in the URL — a filter,
        // a page number, the preview token — went with it.
        sectionUrl(sectionId),
        { cache: "no-store" },
      );
      const data = await response.json();
      return data[sectionId] ?? null;
    } catch {
      return null;
    }
  }

  #showError(message) {
    if (!message) return;
    if (this.#error) {
      this.#error.textContent = message;
      this.#error.hidden = false;
    }
    if (this.#live) this.#live.textContent = message;
  }

  #clearError() {
    if (this.#error) {
      this.#error.textContent = "";
      this.#error.hidden = true;
    }
    if (this.#live) this.#live.textContent = "";
  }
}

if (!customElements.get("biosphere-cart-discount")) {
  customElements.define("biosphere-cart-discount", BiosphereCartDiscount);
}
